import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Amplify } from "aws-amplify";
import { AlertProvider, OverlayContainer, TooltipContainer } from "capitalrx-components-2.0/lib/general";

import App from "@/App";
import {
  AuthenticationProvider,
  ExperienceProvider,
  ExternalLinkModalProvider,
  MemberEligibilityProvider,
  MultifactorAuthenticationContextProvider,
} from "@/App/context";
import "@/scss/styles.scss";

import { enableMultifactorAuthentication, enableSmsMultifactorAuthentication } from "./App/utils/featureFlags";
import "./i18n/i18n";

const cognitoConfig = {
  userPoolId: site.awsConfig.cognito.userPoolId,
  userPoolClientId: site.awsConfig.cognito.userPoolWebClientId,
  userPoolEndpoint: site.awsConfig.cognito.endpoint,
};

if (enableMultifactorAuthentication()) {
  // @ts-ignore
  cognitoConfig["mfa"] = {
    totpEnabled: true,
    smsEnabled: enableSmsMultifactorAuthentication(),
  };
}

Amplify.configure({
  Auth: {
    Cognito: {
      ...cognitoConfig,
    },
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const Root = (): JSX.Element => {
  // @ts-ignore
  return (
    // Query client for using react query
    <QueryClientProvider client={queryClient}>
      {/* required for common components */}
      <OverlayContainer>
        <TooltipContainer>
          <AlertProvider>
            {/* react router */}
            <BrowserRouter>
              {/* our internal contexts */}
              <AuthenticationProvider>
                <MemberEligibilityProvider>
                  <ExperienceProvider>
                    <MultifactorAuthenticationContextProvider>
                      <ExternalLinkModalProvider>
                        {/* and finally the app itself */}
                        <App />
                      </ExternalLinkModalProvider>
                    </MultifactorAuthenticationContextProvider>
                  </ExperienceProvider>
                </MemberEligibilityProvider>
              </AuthenticationProvider>
            </BrowserRouter>
          </AlertProvider>
        </TooltipContainer>
      </OverlayContainer>
    </QueryClientProvider>
  );
};

const container = document.getElementById("root");
const root = createRoot(container);

if (root !== null) {
  root.render(<Root />);
} else {
  throw new Error("Unable to locate app root");
}
